// filepath: c:\Users\rafat\repo\Flaha\FlahaFast\client\src\components\water-quality-visualization\WaterQualityScore.tsx
import React, { useMemo } from 'react';
import { Box, Typography, styled, useTheme, LinearProgress, Paper } from '@mui/material';
import { WaterQualityData } from '../../types/waterQuality';
import { calculateWaterQualityScore } from '../../utils/waterQualityUtils';

interface WaterQualityScoreProps {
  waterData: WaterQualityData;
}

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: theme.typography.fontWeightMedium,
}));

const ScoreContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}));

const ScoreCircle = styled(Box)(({ theme }) => ({
  width: '150px',
  height: '150px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  border: `8px solid ${theme.palette.primary.main}`,
}));

const ScoreNumber = styled(Typography)(({ theme }) => ({
  fontSize: '40px',
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.text.primary,
}));

const ScoreText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

const ScoreLabel = styled(Typography)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(0.5),
}));

const StyledLinearProgress = styled(LinearProgress)({
  height: 10,
  borderRadius: 5,
});

export const WaterQualityScore: React.FC<WaterQualityScoreProps> = ({ waterData }) => {
  const theme = useTheme();
  
  // Calculate water quality scores
  const scores = useMemo(() => {
    return calculateWaterQualityScore(waterData);
  }, [waterData]);
  
  // Get color based on score
  const getScoreColor = (score: number) => {
    if (score >= 80) return theme.palette.success.main;
    if (score >= 60) return theme.palette.info.main;
    if (score >= 40) return theme.palette.warning.main;
    return theme.palette.error.main;
  };
  
  // Get text description of score
  const getScoreDescription = (score: number): string => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Poor';
  };
  
  const scoreColor = getScoreColor(scores.overall);
  
  return (
    <Box sx={{ mb: 4 }}>
      <SectionTitle variant="h6">Water Quality Score</SectionTitle>
      
      <ScoreContainer elevation={2}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
          <Box sx={{ flexBasis: { xs: '100%', md: '30%' }, flexGrow: 0, textAlign: 'center' }}>
            <ScoreCircle sx={{ borderColor: scoreColor }}>
              <ScoreNumber>{scores.overall}</ScoreNumber>
            </ScoreCircle>
            <ScoreText variant="h6">{getScoreDescription(scores.overall)}</ScoreText>
          </Box>
          
          <Box sx={{ flexBasis: { xs: '100%', md: '65%' }, flexGrow: 1 }}>
            <Box sx={{ width: '100%' }}>
              <ScoreLabel>
                <Typography variant="body2">pH Balance</Typography>
                <Typography variant="body2" fontWeight="bold">{scores.phScore}%</Typography>
              </ScoreLabel>
              <StyledLinearProgress 
                variant="determinate" 
                value={scores.phScore} 
                sx={{ mb: 2, bgcolor: theme.palette.grey[200], '& .MuiLinearProgress-bar': { bgcolor: getScoreColor(scores.phScore) } }}
              />
              
              <ScoreLabel>
                <Typography variant="body2">EC/TDS Level</Typography>
                <Typography variant="body2" fontWeight="bold">{scores.ecScore}%</Typography>
              </ScoreLabel>
              <StyledLinearProgress 
                variant="determinate" 
                value={scores.ecScore} 
                sx={{ mb: 2, bgcolor: theme.palette.grey[200], '& .MuiLinearProgress-bar': { bgcolor: getScoreColor(scores.ecScore) } }}
              />
              
              <ScoreLabel>
                <Typography variant="body2">Mineral Balance</Typography>
                <Typography variant="body2" fontWeight="bold">{scores.mineralBalance}%</Typography>
              </ScoreLabel>
              <StyledLinearProgress 
                variant="determinate" 
                value={scores.mineralBalance} 
                sx={{ mb: 2, bgcolor: theme.palette.grey[200], '& .MuiLinearProgress-bar': { bgcolor: getScoreColor(scores.mineralBalance) } }}
              />
              
              <ScoreLabel>
                <Typography variant="body2">Toxicity Risk</Typography>
                <Typography variant="body2" fontWeight="bold">{scores.toxicityRisk}%</Typography>
              </ScoreLabel>
              <StyledLinearProgress 
                variant="determinate" 
                value={scores.toxicityRisk} 
                sx={{ bgcolor: theme.palette.grey[200], '& .MuiLinearProgress-bar': { bgcolor: getScoreColor(scores.toxicityRisk) } }}
              />
            </Box>
          </Box>
        </Box>
      </ScoreContainer>
    </Box>
  );
};

export default WaterQualityScore;
